import { useState, useEffect, useRef, useCallback } from 'react';
import { assignExternalUrl } from '@/utils/url-utils';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import HelpTooltip from '@/components/ui/help-tooltip';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MultiSelect } from '@/components/ui/multi-select';
import { LabelBadges } from '@/components/label-picker';
import { useNotification } from '@/components/notifications';
import * as Sentry from '@sentry/react';
import {
  RunMetricsExportFilters,
  RunMetricsExportStatusResponse,
  getRunMetricsExportStatus,
  listRunMetricsExports,
  startRunMetricsExport,
} from '@/services/admin/export-run-metrics';
import { getOrgLabels } from '@/services/workflow/get-org-labels';
import { ORG_LABELS_QUERY_KEY } from '@/hooks/queries/use-get-org-labels';
import type { Label as WorkflowLabel } from '@/types/workflow-types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_DATE_RANGE_DAYS = 90;
const MAX_POLL_ATTEMPTS = 120; // 12 minutes at 10s intervals
const POLL_INTERVAL_MS = 10_000;

const RUN_STATUS_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'running', label: 'Running' },
  { value: 'queued', label: 'Queued' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function localDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISO(): string {
  return localDateISO(new Date());
}

function oneWeekAgoISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return localDateISO(d);
}

function daysBetween(a: string, b: string): number {
  return Math.abs(
    (new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24),
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusBadge(s: string) {
  const variants: Record<
    string,
    'default' | 'secondary' | 'destructive' | 'outline'
  > = {
    running: 'secondary',
    completed: 'default',
    failed: 'destructive',
  };
  return (
    <Badge variant={variants[s] ?? 'outline'} className='capitalize'>
      {s}
    </Badge>
  );
}

function formatDateRange(filters?: RunMetricsExportFilters | null): string {
  if (!filters?.start_date || !filters?.end_date) return '—';
  return `${filters.start_date.replace(/-/g, '/')} – ${filters.end_date.replace(/-/g, '/')}`;
}

function formatStatusFilter(filters?: RunMetricsExportFilters | null): string {
  if (!filters?.status_filter?.length) return 'All';
  return filters.status_filter
    .map((v) => RUN_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)
    .join(', ');
}

function resolveFilterLabels(
  filters: RunMetricsExportFilters | null | undefined,
  labels: WorkflowLabel[],
): WorkflowLabel[] {
  if (!filters?.label_ids?.length) return [];
  return filters.label_ids
    .map((id) => labels.find((l) => l.id === id))
    .filter((l): l is WorkflowLabel => l !== undefined);
}

function labelFilterCell(
  filters: RunMetricsExportFilters | null | undefined,
  labels: WorkflowLabel[],
) {
  if (!filters?.label_ids?.length) {
    return <span className='text-xs text-muted-foreground'>All</span>;
  }
  const filterLabels = resolveFilterLabels(filters, labels);
  if (filterLabels.length === 0) {
    const n = filters.label_ids.length;
    return (
      <span className='text-xs text-muted-foreground'>
        {n} label{n !== 1 ? 's' : ''}
      </span>
    );
  }
  return <LabelBadges labels={filterLabels} maxVisible={2} monochrome />;
}

function formatIncludeCost(filters?: RunMetricsExportFilters | null): string {
  return filters?.include_cost === false ? 'No' : 'Yes';
}

function filterCell(text: string) {
  return (
    <span
      className='text-xs text-muted-foreground line-clamp-2 whitespace-normal'
      title={text}
    >
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ExportMetricsDialogProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportMetricsDialog({
  orgId,
  open,
  onOpenChange,
}: ExportMetricsDialogProps) {
  const notification = useNotification();

  // Filter state
  const [startDate, setStartDate] = useState(oneWeekAgoISO);
  const [endDate, setEndDate] = useState(todayISO);
  const [includeCost, setIncludeCost] = useState(true);
  const [includeAggregates, setIncludeAggregates] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [dateError, setDateError] = useState<string | null>(null);

  // Export state
  const [activeExportId, setActiveExportId] = useState<string | null>(null);
  const activeExportIdRef = useRef<string | null>(null);
  const [startingExport, setStartingExport] = useState(false);
  const pollCountRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const orgIdRef = useRef(orgId);

  useEffect(() => {
    activeExportIdRef.current = activeExportId;
  }, [activeExportId]);

  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  // History
  const [history, setHistory] = useState<RunMetricsExportStatusResponse[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const activeJob = activeExportId
    ? history.find((j) => j.export_id === activeExportId)
    : null;
  const generating =
    startingExport ||
    (activeExportId !== null && (!activeJob || activeJob.status === 'running'));

  const { data: orgLabels = [] } = useQuery<WorkflowLabel[]>({
    queryKey: [ORG_LABELS_QUERY_KEY, orgId],
    queryFn: async () => {
      const response = await getOrgLabels(orgId);
      return response.data?.labels ?? [];
    },
    enabled: open && !!orgId,
  });

  // ------------------------------------------------------------------
  // Load history when dialog opens
  // ------------------------------------------------------------------
  const loadHistory = useCallback(async () => {
    if (!orgId) return;
    const capturedOrgId = orgId;
    setHistoryLoading(true);
    try {
      const res = await listRunMetricsExports(capturedOrgId);
      if (orgIdRef.current !== capturedOrgId) return;
      const serverExports = res.exports ?? [];
      const serverIds = new Set(serverExports.map((e) => e.export_id));
      setHistory((prev) => {
        // Preserve optimistic in-flight entries not yet reflected on the server
        const preserved = prev.filter((j) => !serverIds.has(j.export_id));
        // Protect client-confirmed completion; let server override a client-side failure
        const merged = serverExports.map((serverJob) => {
          const localJob = prev.find(
            (j) => j.export_id === serverJob.export_id,
          );
          if (localJob?.status === 'completed') return localJob;
          return serverJob;
        });
        return [...preserved, ...merged];
      });
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'export-run-metrics-load-history' },
      });
    } finally {
      setHistoryLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  // Stop polling once the active job reaches a terminal state (e.g. via Refresh).
  // Also fires the download/notification for the Refresh path — the poll path
  // already handles this, and calls setActiveExportId(null) in the same batch
  // as setHistory, so this effect sees activeExportId === null and returns early.
  useEffect(() => {
    if (!activeExportId) return;
    const job = history.find((j) => j.export_id === activeExportId);
    if (job?.status === 'completed') {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setActiveExportId(null);
      notification.success(
        `Export ready! ${job.row_count ?? 0} row(s) exported.`,
      );
      if (job.download_url) {
        assignExternalUrl(job.download_url);
      }
    } else if (job?.status === 'failed') {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setActiveExportId(null);
      notification.error(job.error ?? 'Export failed. Please try again.');
    }
  }, [history, activeExportId, notification]);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Reset poll state and org-specific filters when the org changes
  useEffect(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollCountRef.current = 0;
    setActiveExportId(null);
    setHistory([]);
    setLabelFilter([]);
    setStatusFilter([]);
  }, [orgId]);

  // ------------------------------------------------------------------
  // Date validation
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!startDate || !endDate) {
      setDateError('Both dates are required.');
      return;
    }
    if (startDate > endDate) {
      setDateError('Start date must be before end date.');
      return;
    }
    if (daysBetween(startDate, endDate) > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days.`);
      return;
    }
    setDateError(null);
  }, [startDate, endDate]);

  // ------------------------------------------------------------------
  // Polling
  // ------------------------------------------------------------------
  const pollStatus = useCallback(
    async (exportId: string) => {
      const capturedOrgId = orgId;
      pollCountRef.current += 1;

      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        clearTimeout(pollTimerRef.current!);
        if (activeExportIdRef.current === exportId) {
          setActiveExportId(null);
          setHistory((prev) =>
            prev.map((j) =>
              j.export_id === exportId && j.status === 'running'
                ? { ...j, status: 'failed', error: 'Export timed out.' }
                : j,
            ),
          );
          notification.error('Export timed out. Please try again.');
        }
        return;
      }

      try {
        const status = await getRunMetricsExportStatus(capturedOrgId, exportId);

        // Discard result if org changed while the request was in-flight
        if (orgIdRef.current !== capturedOrgId) return;

        // Don't overwrite a terminal status with a stale intermediate response
        setHistory((prev) =>
          prev.map((j) => {
            if (j.export_id !== exportId) return j;
            if (j.status === 'completed' || j.status === 'failed') return j;
            return status;
          }),
        );

        if (status.status === 'completed') {
          clearTimeout(pollTimerRef.current!);
          if (activeExportIdRef.current === exportId) {
            setActiveExportId(null);
            notification.success(
              `Export ready! ${status.row_count ?? 0} row(s) exported.`,
            );
            if (status.download_url) {
              assignExternalUrl(status.download_url);
            }
          }
        } else if (status.status === 'failed') {
          clearTimeout(pollTimerRef.current!);
          if (activeExportIdRef.current === exportId) {
            setActiveExportId(null);
            notification.error(
              status.error ?? 'Export failed. Please try again.',
            );
          }
        } else {
          pollTimerRef.current = setTimeout(
            () => pollStatus(exportId),
            POLL_INTERVAL_MS,
          );
        }
      } catch (err) {
        if (orgIdRef.current !== capturedOrgId) return;
        Sentry.captureException(err, {
          extra: { context: 'export-run-metrics-poll' },
        });
        // retry on transient errors
        pollTimerRef.current = setTimeout(
          () => pollStatus(exportId),
          POLL_INTERVAL_MS,
        );
      }
    },
    [orgId, notification],
  );

  // ------------------------------------------------------------------
  // Start export
  // ------------------------------------------------------------------
  const handleGenerate = useCallback(async () => {
    if (generating || dateError) return;

    setStartingExport(true);
    pollCountRef.current = 0;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);

    const filters: RunMetricsExportFilters = {
      start_date: startDate,
      end_date: endDate,
      include_cost: includeCost,
      include_aggregates: includeAggregates,
      status_filter: statusFilter.length ? statusFilter : undefined,
      label_ids: labelFilter.length ? labelFilter : undefined,
    };

    try {
      const { export_id } = await startRunMetricsExport(orgId, filters);

      setActiveExportId(export_id);

      // Optimistically add to history
      setHistory((prev) => [
        {
          export_id,
          status: 'running',
          created_at: new Date().toISOString(),
          filters,
        },
        ...prev,
      ]);

      notification.info(
        'Export started — generating workbook in the background.',
      );
      void pollStatus(export_id);
    } catch (err) {
      setActiveExportId(null);
      Sentry.captureException(err, {
        extra: { context: 'export-run-metrics-start' },
      });
      notification.error(
        err instanceof Error ? err.message : 'Failed to start export.',
      );
    } finally {
      setStartingExport(false);
    }
  }, [
    generating,
    dateError,
    startDate,
    endDate,
    includeCost,
    includeAggregates,
    statusFilter,
    labelFilter,
    orgId,
    pollStatus,
    notification,
  ]);

  // ------------------------------------------------------------------
  // Re-download a completed export
  // ------------------------------------------------------------------
  const handleDownload = useCallback(
    async (exportId: string) => {
      setDownloadingId(exportId);
      try {
        const status = await getRunMetricsExportStatus(orgId, exportId);
        if (status.download_url) {
          assignExternalUrl(status.download_url);
        } else {
          notification.warning('Download URL not available for this export.');
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'export-run-metrics-download' },
        });
        notification.error('Failed to get download link.');
      } finally {
        setDownloadingId(null);
      }
    },
    [orgId, notification],
  );

  const canGenerate = !generating && !dateError && !!startDate && !!endDate;

  return (
    <Modal
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      title={
        <span className='flex items-center gap-2'>
          <BarChart3 className='size-5' />
          Export Run Metrics
        </span>
      }
      description='Downloads an XLSX workbook. With summary sheets on: "By Workflow" and "By Label" aggregate stats, plus a "Run Details" sheet with one row per run.'
      contentClassName='w-fit max-w-[95vw] sm:max-w-[95vw] min-w-[350px] sm:min-w-[850px]'
    >
      <div className='space-y-6 py-2'>
        {/* Filters */}
        <section className='space-y-4'>
          <h3 className='text-sm font-medium text-foreground'>Filters</h3>

          {/* Date range */}
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1.5 w-fit'>
              <Label htmlFor='export-start-date'>Start date</Label>
              <Input
                id='export-start-date'
                type='date'
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className='space-y-1.5 w-fit'>
              <Label htmlFor='export-end-date'>End date</Label>
              <Input
                id='export-end-date'
                type='date'
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          {dateError && <p className='text-xs text-destructive'>{dateError}</p>}

          {/* Status + label filters */}
          <div className='grid grid-cols-1 items-start gap-3 sm:grid-cols-2'>
            <div className='min-w-0 h-[62px] space-y-1.5 overflow-visible'>
              <div className='flex h-5 min-w-0 items-center gap-2'>
                <Label className='whitespace-nowrap'>Run status</Label>
                <HelpTooltip label='Run status help'>
                  Leave empty to include all run statuses.
                </HelpTooltip>
              </div>
              <MultiSelect
                items={RUN_STATUS_OPTIONS}
                selectedValues={statusFilter}
                onSelectionChange={setStatusFilter}
                getItemValue={(i) => i.value}
                getItemLabel={(i) => i.label}
                placeholder='All statuses'
                pluralLabel='statuses'
                singularLabel='status'
              />
            </div>
            <div className='min-w-0 h-[62px] space-y-1.5 overflow-visible'>
              <div className='flex h-5 min-w-0 items-center gap-2'>
                <Label className='whitespace-nowrap'>Workflow labels</Label>
                <HelpTooltip label='Workflow labels help'>
                  Includes runs from workflows tagged with any selected label.
                </HelpTooltip>
              </div>
              <MultiSelect
                items={orgLabels}
                selectedValues={labelFilter}
                onSelectionChange={setLabelFilter}
                getItemValue={(label) => label.id}
                getItemLabel={(label) => label.name}
                placeholder='All labels'
                pluralLabel='labels'
                singularLabel='label'
              />
            </div>
          </div>

          {/* Options toggles */}
          <div className='flex flex-wrap gap-3'>
            <div className='flex items-center justify-between rounded-md border px-4 py-3'>
              <div className='pr-5'>
                <p className='text-sm font-medium'>Include summary sheets</p>
                <p className='text-xs text-muted-foreground'>
                  Adds aggregate sheets to the workbook
                </p>
              </div>
              <Switch
                checked={includeAggregates}
                onCheckedChange={setIncludeAggregates}
                aria-label='Include summary sheets'
              />
            </div>
            <div className='flex items-center justify-between rounded-md border px-4 py-3'>
              <div className='pr-5'>
                <p className='text-sm font-medium'>Include cost & tokens</p>
                <p className='text-xs text-muted-foreground'>
                  Adds cost/token data per run
                </p>
              </div>
              <Switch
                checked={includeCost}
                onCheckedChange={setIncludeCost}
                aria-label='Include cost & tokens'
              />
            </div>
          </div>
        </section>

        {/* Generate button */}
        <div className='flex flex-wrap items-center gap-3'>
          <Button
            onClick={handleGenerate}
            disabled={!canGenerate}
            className='w-fit shrink-0'
          >
            {generating ? (
              <>
                <Loader2 className='size-4 mr-2 animate-spin' />
                Generating…
              </>
            ) : (
              <>
                <Download className='size-4 mr-2' />
                Generate & Download
              </>
            )}
          </Button>

          {generating && (
            <p className='text-xs text-muted-foreground'>
              Export running in the background
            </p>
          )}
        </div>

        {/* Export history */}
        <section className='space-y-2'>
          <div className='flex items-center justify-between'>
            <h3 className='text-sm font-medium text-foreground'>
              Recent exports
            </h3>
            <Button
              variant='ghost'
              size='sm'
              onClick={loadHistory}
              disabled={historyLoading}
            >
              <RefreshCw
                className={`size-3.5 mr-1 ${historyLoading ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>

          {historyLoading && history.length === 0 ? (
            <div className='flex items-center justify-center py-6 text-muted-foreground text-sm'>
              <Loader2 className='size-4 mr-2 animate-spin' />
              Loading…
            </div>
          ) : history.length === 0 ? (
            <p className='text-sm text-muted-foreground py-4 text-center'>
              No exports yet.
            </p>
          ) : (
            <div className='rounded-md border overflow-hidden'>
              <Table tableRef={tableRef}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Export Job Started</TableHead>
                    <TableHead>Date range</TableHead>
                    <TableHead>Run status</TableHead>
                    <TableHead>Labels</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className='text-right'>Rows</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((job) => (
                    <TableRow key={job.export_id}>
                      <TableCell className='text-sm whitespace-nowrap'>
                        {formatDate(job.created_at)}
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>
                        {filterCell(formatDateRange(job.filters))}
                      </TableCell>
                      <TableCell>
                        {filterCell(formatStatusFilter(job.filters))}
                      </TableCell>
                      <TableCell className='sm:max-w-[200px] max-w-[350px]'>
                        {labelFilterCell(job.filters, orgLabels)}
                      </TableCell>
                      <TableCell className='text-xs text-muted-foreground whitespace-nowrap'>
                        {formatIncludeCost(job.filters)}
                      </TableCell>
                      <TableCell>{statusBadge(job.status)}</TableCell>
                      <TableCell className='text-right text-sm tabular-nums'>
                        {job.row_count != null
                          ? job.row_count.toLocaleString()
                          : '—'}
                      </TableCell>
                      <TableCell className='text-right'>
                        {job.status === 'completed' && (
                          <Button
                            variant='ghost'
                            size='sm'
                            aria-label='Download export'
                            onClick={() => handleDownload(job.export_id)}
                            disabled={downloadingId === job.export_id}
                          >
                            {downloadingId === job.export_id ? (
                              <Loader2 className='size-3.5 animate-spin' />
                            ) : (
                              <Download className='size-3.5' />
                            )}
                          </Button>
                        )}
                        {job.status === 'failed' && job.error && (
                          <span
                            className='text-xs text-destructive'
                            title={job.error}
                          >
                            Error
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
